import React, { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import MyButton from '../../../../shared-components/button/Button';
import CustomInput from '../../../../shared-components/custom-input/CustomInput';
import ModalBox from '../../../../shared-components/modal-box/ModalBox';

export interface StopReasonModalProperties {
  open: boolean;
  loading: boolean;
  sessionCount?: number;
  onClose: () => void;
  onSubmit: (reason: string) => void;
}

/**
 * Stop reason confirmation modal component.
 * Prompts user for a mandatory reason before remotely stopping sessions.
 * @param {StopReasonModalProperties} properties - Component properties.
 * @param {boolean} properties.open - Modal visibility flag.
 * @param {boolean} properties.loading - In-flight API loading indicator.
 * @param {number} [properties.sessionCount] - Number of sessions being stopped.
 * @param {() => void} properties.onClose - Modal close handler.
 * @param {(reason: string) => void} properties.onSubmit - Submission callback receiving trimmed reason.
 * @returns {React.JSX.Element} Rendered modal dialog.
 */
function StopReasonModalComponent({
  open,
  loading,
  sessionCount = 1,
  onClose,
  onSubmit,
}: Readonly<StopReasonModalProperties>): React.JSX.Element {
  const { t } = useTranslation('transaction');
  const [reason, setReason] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    if (open) {
      setReason('');
      setErrorMessage('');
    }
  }, [open]);

  /**
   * Handles reason input value changes and clears validation errors.
   * @param {string} _name - The name of the input field.
   * @param {string | number} value - The updated input value.
   */
  const handleReasonChange = (_name: string, value: string | number) => {
    const stringValue = String(value);
    setReason(stringValue);
    if (errorMessage && stringValue.trim().length > 0) {
      setErrorMessage('');
    }
  };

  /**
   * Validates and submits the reason for stopping the session.
   */
  const handleSubmit = () => {
    const trimmedReason = reason.trim();
    if (!trimmedReason) {
      setErrorMessage(
        t('stopReasonRequired', {
          defaultValue: 'Please enter a reason for stopping the session.',
        }),
      );
      return;
    }
    onSubmit(trimmedReason);
  };

  /**
   * Handles modal cancellation preventing dismiss during active network requests.
   */
  const handleCancel = () => {
    if (!loading) {
      onClose();
    }
  };

  const modalTitle =
    sessionCount > 1
      ? t('stopChargingSessionsTitle', { defaultValue: 'Stop Charging Sessions' })
      : t('stopChargingSessionTitle', { defaultValue: 'Stop Charging Session' });

  return (
    <ModalBox
      open={open}
      title={modalTitle}
      closeModal={handleCancel}
      header
      content={
        <form
          className="stop-reason-modal-content"
          onSubmit={(event) => {
            event.preventDefault();
            handleSubmit();
          }}>
          <p className="stop-reason-description">
            {sessionCount > 1
              ? t('stopMultipleSessionsConfirmDescription', {
                  defaultValue:
                    'Are you sure you want to stop the selected charging sessions? Please provide a reason to proceed.',
                })
              : t('stopSessionConfirmDescription', {
                  defaultValue:
                    'Are you sure you want to stop this active charging session? Please provide a reason to proceed.',
                })}
          </p>

          <div className="stop-reason-input-wrap">
            <CustomInput
              name="stopReason"
              type="text"
              inputLabel={t('enterReasonForStopping', {
                defaultValue: 'Reason for stopping',
              })}
              placeholder={t('enterReasonForStoppingPlaceholder', {
                defaultValue: 'Please enter the reason for stopping this session',
              })}
              value={reason}
              onChangeValue={handleReasonChange}
              valueRequired
              error={Boolean(errorMessage)}
              errorMessage={errorMessage}
              disabled={loading}
              autoFocus
            />
          </div>

          <div className="stop-reason-modal-actions">
            <MyButton
              label={t('cancel', { defaultValue: 'Cancel' })}
              buttonType="primary-outline"
              onClickFunc={handleCancel}
              disableButton={loading}
            />
            <MyButton
              label={t('submit', { defaultValue: 'Submit' })}
              buttonType="error"
              onClickFunc={handleSubmit}
              loading={loading}
              disableButton={loading}
            />
          </div>
        </form>
      }
    />
  );
}

export const StopReasonModal = memo(StopReasonModalComponent);
export default StopReasonModal;
